import React, { PureComponent } from 'react';
import { array, bool, func, object, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import clsx from 'clsx';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { timestampToDate } from '../../util/dates';
import { propTypes } from '../../util/types';
import config from '../../config';
import { Form, IconSpinner, PrimaryButton } from '../../components';
import EstimatedBreakdownMaybe from './EstimatedBreakdownMaybe';
import FieldDateAndTimeInput from './FieldDateAndTimeInput';

import css from './BookingTimeForm.module.css';

export class BookingTimeFormComponent extends PureComponent {
  constructor(props) {
    super(props);

    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleOnChange = this.handleOnChange.bind(this);
    this.state = {
      group_tour_over_max_size: false,
      hideBreakdown: false,
      loading: false 
    }
  }

  componentDidUpdate(){
    // if fetching the line items is in progress, then we set a timer to change the state aafter 750 ms
    // this means that the request is still incomplete so then we show th loading component and hide the bookingBreakdown
    // component. Else we set the state to hide the breakdown and show the loading 
    if(this.props.fetchLineItemsInProgress){
      const timer = setTimeout(() =>{
        if(this.props.fetchLineItemsInProgress){
          this.setState({
            hideBreakdown: true,
            loading: true
          });
        }
      }, 750)
      return clearTimeout(timer)
    } else {
      this.setState({
        hideBreakdown: false,
        loading: false
      });
    }
  }

  handleFormSubmit(e) {
    this.props.onSubmit(e);
  }

  // When the values of the form are updated we need to fetch
  // lineItems from FTW backend for the EstimatedTransactionMaybe
  // In case you add more fields to the form, make sure you add
  // the values here to the bookingData object.
  handleOnChange(formValues) {
    const {
      bookingStartTime,
      bookingEndTime,
      bookingQuantityAdult,
      bookingQuantityChild,
      bookingQuantityInfant,
    } = formValues.values;
    const startDate = bookingStartTime ? timestampToDate(bookingStartTime) : null;
    const endDate = bookingEndTime ? timestampToDate(bookingEndTime) : null;

    const listingId = this.props.listingId;
    const isOwnListing = this.props.isOwnListing;
    const isGroupTour = this.props.publicData && this.props.publicData.groupTour ? this.props.publicData.groupTour : false ;

    if(bookingStartTime && bookingEndTime && isGroupTour){
      const groupTourSize = this.props.publicData.groupTourSize
      // sum the booking quanities
      let groupTourPeople = bookingQuantityAdult + bookingQuantityChild + bookingQuantityInfant   
      // depending on the total number of selected people change the state of the max capacity prop.
      if(groupTourPeople > groupTourSize ){
        this.setState({
          group_tour_over_max_size: true
        })
      } else{
        this.setState({
          group_tour_over_max_size: false
        })
      }
    }

    if (bookingStartTime && bookingEndTime && !this.props.fetchLineItemsInProgress && !this.state.group_tour_over_max_size) {

      // We expect values bookingStartTime and bookingEndTime to be strings
      // which is the default case when the value has been selected through the form
      const isSameTime = bookingStartTime === bookingEndTime;

      if (bookingStartTime && bookingEndTime && !isSameTime && !this.props.fetchLineItemsInProgress) {
        this.props.onFetchTransactionLineItems({
          bookingData: {
            startDate,
            endDate,
            bookingQuantityAdult,
            bookingQuantityChild,
            bookingQuantityInfant,
          },
          listingId,
          isOwnListing,
        });
      }
    }
  }

  render() {
    const { rootClassName, className, price: unitPrice, priceChild, publicData, ...rest } = this.props;
    const classes = clsx(rootClassName || css.root, className);
    if (!unitPrice) {
      return (
        <div className={classes}>
          <p className={css.error}>
            <FormattedMessage id="BookingTimeForm.listingPriceMissing" />
          </p>
        </div>
      );
    }
    if (unitPrice.currency !== config.currency) {
      return (
        <div className={classes}>
          <p className={css.error}>
            <FormattedMessage id="BookingTimeForm.listingCurrencyInvalid" />
          </p>
        </div>
      );
    }

    return (
      <FinalForm
        {...rest}
        publicData={publicData}
        unitPrice={unitPrice}
        priceChild={priceChild}
        onSubmit={this.handleFormSubmit}
        render={fieldRenderProps => {
          const {
            endDatePlaceholder,
            startDatePlaceholder,
            form,
            pristine,
            handleSubmit,
            intl,
            isOwnListing,
            listingId,
            submitButtonWrapperClassName,
            unitType,
            values,
            monthlyTimeSlots,
            onFetchTimeSlots,
            timeZone,
            lineItems,
            //fetchLineItemsInProgress,
            fetchLineItemsError,
            publicData,
          } = fieldRenderProps;

          const startTime = values && values.bookingStartTime ? values.bookingStartTime : null;
          const endTime = values && values.bookingEndTime ? values.bookingEndTime : null;
          const bookingQuantityAdult =
            values && values.bookingQuantityAdult ? values.bookingQuantityAdult : null;
          const bookingQuantityChild =
            values && values.bookingQuantityChild ? values.bookingQuantityChild : null;
          const bookingQuantityInfant =
            values && values.bookingQuantityInfant ? values.bookingQuantityInfant : null;
          const childAgeGroup = 
            publicData && publicData.childAgeGroup ? publicData.childAgeGroup: {min: 2, max: 12};
          const infantAgeGroup = 
            publicData && publicData.infantAgeGroup ? publicData.infantAgeGroup : {min: 0, max: 2};

          const bookingStartLabel = intl.formatMessage({
            id: 'BookingTimeForm.bookingStartTitle',
          });

          const bookingEndLabel = intl.formatMessage({
            id: 'BookingTimeForm.bookingEndTitle',
          });
          // the minimum age for adult is the maximum age for children
          const bookingNumberAdultLabel = intl.formatMessage({
            id: 'BookingTimeForm.bookingNumberAdultLabel',
          },
          {
            minAdultAge: childAgeGroup.max
          });

          const bookingNumberChildLabel = intl.formatMessage({
            id: 'BookingTimeForm.bookingNumberChildLabel',
          },
          {
            minAge: childAgeGroup.min,
            maxAge: childAgeGroup.max
          });

          const bookingNumberInfantLabel = intl.formatMessage({
            id: 'BookingTimeForm.bookingNumberInfantLabel',
          },
          {
            minAge: infantAgeGroup.min,
            maxAge: infantAgeGroup.max
          });

          const bookingNumberAdultPlaceholder = intl.formatMessage({
            id: 'BookingTimeForm.bookingNumberAdultPlaceholder',
          });

          const bookingNumberChildPlaceholder = intl.formatMessage({
            id: 'BookingTimeForm.bookingNumberChildPlaceholder',
          });

          const bookingNumberInfantPlaceholder = intl.formatMessage({
            id: 'BookingTimeForm.bookingNumberInfantPlaceholder',
          });

          const startDate = startTime ? timestampToDate(startTime) : null;
          const endDate = endTime ? timestampToDate(endTime) : null;

          // This is the place to collect breakdown estimation data. See the
          // EstimatedBreakdownMaybe component to change the calculations
          // for customized payment processes.
          const bookingData =
            startDate && endDate
              ? {
                  unitType,
                  startDate,
                  endDate,
                  timeZone,
                  bookingQuantityAdult,
                  bookingQuantityChild,
                  bookingQuantityInfant,
                }
              : null;

          const showEstimatedBreakdown =
            bookingData && lineItems && !fetchLineItemsError && !this.state.group_tour_over_max_size && !this.state.hideBreakdown;

          const bookingInfoMaybe = showEstimatedBreakdown ? (
            <div className={css.priceBreakdownContainer}>
              <h3 className={css.priceBreakdownTitle}>
                <FormattedMessage id="BookingTimeForm.priceBreakdownTitle" />
              </h3>
              <EstimatedBreakdownMaybe
                bookingData={bookingData}
                lineItems={lineItems}
                publicData={publicData}
              />
            </div>
          ) : null;

          const loadingSpinnerMaybe = this.state.loading ? (
            <IconSpinner className={css.spinner} />
          ) : null;

          const bookingInfoErrorMaybe = fetchLineItemsError ? (
            <span className={css.sideBarError}>
              <FormattedMessage id="BookingDatesForm.fetchLineItemsError" />
            </span>
          ) : null;

          const bookingGroupTourOverCapacityMaybe = this.state.group_tour_over_max_size ? (
            <span className={css.sideBarError}>
              <FormattedMessage id="BookingDatesForm.groupTourMaximumCapacity"
              values={{ maxPeople: this.props.publicData.groupTourSize }}
               />
            </span>
          ) : null;

          const submitButtonClasses = clsx(
            submitButtonWrapperClassName || css.submitButtonWrapper
          );

          const startDateInputProps = {
            label: bookingStartLabel,
            placeholderText: startDatePlaceholder,
          };
          const endDateInputProps = {
            label: bookingEndLabel,
            placeholderText: endDatePlaceholder,
          };
          const adultProps = {
            label: bookingNumberAdultLabel,
            placeholderText: bookingNumberAdultPlaceholder,
          };
          const childProps = {
            label: bookingNumberChildLabel,
            placeholderText: bookingNumberChildPlaceholder,
          };
          const infantProps = {
            label: bookingNumberInfantLabel,
            placeholderText: bookingNumberInfantPlaceholder,
          };

          const dateInputProps = {
            startDateInputProps,
            endDateInputProps,
            adultProps,
            childProps,
            infantProps,
          };
          const formValid = form.getState().invalid;
          return (
            <Form onSubmit={handleSubmit} className={classes} enforcePagePreloadFor="CheckoutPage">
              <FormSpy
                subscription={{ values: true,  }}
                onChange={values => {
                  this.handleOnChange(values, fieldRenderProps);
                }}
              />
              {monthlyTimeSlots && timeZone ? (
                <FieldDateAndTimeInput
                  {...dateInputProps}
                  className={css.bookingDates}
                  listingId={listingId}
                  bookingStartLabel={bookingStartLabel}
                  onFetchTimeSlots={onFetchTimeSlots}
                  monthlyTimeSlots={monthlyTimeSlots}
                  values={values}
                  intl={intl}
                  form={form}
                  pristine={pristine}
                  timeZone={timeZone}
                />
              ) : null}

              {bookingInfoMaybe}
              {loadingSpinnerMaybe}
              {bookingInfoErrorMaybe}
              {bookingGroupTourOverCapacityMaybe}

              <p className={css.smallPrint}>
                <FormattedMessage
                  id={
                    isOwnListing
                      ? 'BookingTimeForm.ownListing'
                      : 'BookingTimeForm.youWontBeChargedInfo'
                  }
                />
              </p>
              <div className={submitButtonClasses}>
                <PrimaryButton type="submit" disabled={formValid}>
                  <FormattedMessage id="BookingTimeForm.requestToBook" />
                </PrimaryButton>
              </div>
            </Form>
          );
        }}
      />
    );
  }
}

BookingTimeFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  submitButtonWrapperClassName: null,
  price: null,
  isOwnListing: false,
  listingId: null,
  startDatePlaceholder: null,
  endDatePlaceholder: null,
  monthlyTimeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
  group_tour_over_max_size:false,
};

BookingTimeFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  submitButtonWrapperClassName: string,

  unitType: propTypes.bookingUnitType.isRequired,
  price: propTypes.money,
  isOwnListing: bool,
  listingId: propTypes.uuid,
  monthlyTimeSlots: object,
  onFetchTimeSlots: func.isRequired,

  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,

  // for tests
  startDatePlaceholder: string,
  endDatePlaceholder: string,
};

const BookingTimeForm = compose(injectIntl)(BookingTimeFormComponent);
BookingTimeForm.displayName = 'BookingTimeForm';

export default BookingTimeForm;
